import { useEffect, useState } from 'react';
import { Channel, DefaultGenerics, StreamChat } from 'stream-chat';
import { Channel as ChannelUI, Chat,  Streami18n, useChatContext } from 'stream-chat-react';
import { EmojiPicker } from 'stream-chat-react/emojis';
import data from '@emoji-mart/data';
import { init, SearchIndex } from 'emoji-mart';
import 'stream-chat-react/dist/css/v2/index.css';
import './styles/index.css';

import daLang from './locales/da.json';
import deLang from './locales/de.json';
import enLang from './locales/en.json';
import esLang from './locales/es.json';
import fiLang from './locales/fi.json';
import frLang from './locales/fr.json';
import nbLang from './locales/nb.json';
import svLang from './locales/sv.json';

import {
  ChannelInner,
  MessagingSidebar,
  MessagingThreadHeader,
  SendButton,
} from './components';

import { GiphyContextProvider, useThemeContext } from './context';
import { useConnectUser, useChecklist, useMobileView, useUpdateAppHeightOnResize } from './hooks';
import Interop from './interfaces/Interop';
import DateSeparatorCustom from './components/DateSeparator/DateSeparatorCustom';
import { getChannelListOptions } from './channelListOptions';
import { ApplicationColor } from './models/ApplicationColor';
import { ChatPageNavigation } from './models/ChatPageNavigation';
import { ChatPageType } from './enums/ChatPageType';
import { ClientCredential } from './models/ClientCredential';
import HybridWebView from './hybridWebView/HybridWebView';
import { ModuleType } from './enums/ModuleType';
import { useChannelContext } from '../src/components/ChannelContext/ChannelContext'; // Import your context
import { BlockUnblockProvider } from '../src/context/BlockUnblock';
import { AlertContextProvider } from '../src/context/AlertContext';
import './SuggestionListWithHeader';
import { SuggestionListWithHeader } from './SuggestionListWithHeader';
import { CustomAutocompleteSuggestionItem } from './components/CustomAutocompleteSuggestionItem/CustomAutocompleteSuggestionItem';
import './components/CustomAutocompleteSuggestionItem/CustomAutocompleteSuggestionItem.css';
import { Refresh } from './assets/Refresh';
import LoadingSpinner from "../src/assets/LoadingSpinner.gif";
import { ChatType } from './enums/ChatType';

init({ data });

type AppProps = {
  apiKey: string;
  credentials: ClientCredential;
  targetOrigin: string;
  colors: ApplicationColor;
  pageType: ChatPageNavigation;
  fontSize: Number;
  platformTypes: string;
  interop: Interop;
  language: string;
  onRemount: () => void;
};

/**
 * Emoji picker component with theme support
 * Wraps the Stream Chat EmojiPicker component and passes current theme.
 * This ensures emoji picker matches the app's light/dark theme.
 */
const WrappedEmojiPicker = () => {
  const { theme } = useThemeContext();
  return <EmojiPicker pickerProps={{ theme }} />;
};

/**
 * SET COLORS - Apply custom color scheme to app CSS variables
 * Takes ApplicationColor object from native/web and applies colors to CSS custom properties.
 * Handles both light and dark backgrounds by swapping foreground/background if needed.
 */
function setColors(colors: ApplicationColor) {
  const backgroundColor = colors.TopNavBackgroundColor;
  const foregroundColor = colors.TopNavForegroundColor;
  
  // Check if background is light (not dark) and swap colors for better contrast
  if (!isColorDark(parseColor(colors.TopNavBackgroundColor))) {
    // Light background - swap colors (use foreground as background)
    document.documentElement.style.setProperty('--app_background-color', foregroundColor);
    document.documentElement.style.setProperty('--app_forgrond-color', backgroundColor);
    document.documentElement.style.setProperty('--messaging__channel-list_header_background_color', backgroundColor);
    document.documentElement.style.setProperty('--messaging__channel-foreground_color', foregroundColor);
  } else {
    // Dark background - use colors as-is
    document.documentElement.style.setProperty('--app_background-color', backgroundColor);
    document.documentElement.style.setProperty('--app_forgrond-color', foregroundColor);
    document.documentElement.style.setProperty('--messaging__channel-list_header_background_color', backgroundColor);
    document.documentElement.style.setProperty('--messaging__channel-foreground_color', foregroundColor);
  }

  // Set CTA (Call-to-Action) button colors - always applied regardless of theme
  document.documentElement.style.setProperty('--app_cta_foregrond-color', colors.CtaFontColor);
  document.documentElement.style.setProperty('--app_cta_background-color', colors.CtaBackgroundColor);
}

/**
 * PARSE COLOR - Convert hex color string to RGB object
 * Takes a hex color string and converts it to RGB values.
 * Used by isColorDark() to determine if a color is light or dark.
 * 
 * @param colorHex - Hex color string starting with # 
 * @returns Object with r, g, b properties (0-255 range)
 */
function parseColor(colorHex: string) {
  const r = parseInt(colorHex.slice(1, 3), 16);
  const g = parseInt(colorHex.slice(3, 5), 16);
  const b = parseInt(colorHex.slice(5, 7), 16);
  return { r, g, b };
}

/**
 * IS COLOR DARK - Determine if a color is perceived as dark or light
 * 
 * Uses luminance formula to calculate perceived brightness.
 * Helps decide whether to use light or dark text on a colored background.
 * ALGORITHM:
 * Uses weighted RGB formula: darkness = 1 - (0.299*R + 0.587*G + 0.114*B) / 255
 * - Green contributes most to perceived brightness (0.587 weight)
 * - Red contributes moderately (0.299 weight)
 * - Blue contributes least (0.114 weight)
 * @param color - RGB color object with r, g, b properties (0-255)
 * @returns true if color is dark (darkness > 0.20), false if light
 */
function isColorDark(color: { r: any; g: any; b: any; }) {
  const darkness = 1 - (0.299 * color.r + 0.587 * color.g + 0.114 * color.b) / 255;
  return darkness > 0.20; 
}

/**
 * SET FONT SIZE - Apply font size scaling for accessibility
 * Sets CSS custom property for text size scaling throughout the app.
 * Allows users to increase/decrease text size for better readability.
 * @param size - Font size as percentage
 */
function setFontSize(size: Number) {
  document.documentElement.style.setProperty('--text-size', `${size}%`);
}

/**
 * Create a new direct message (DM) channel between two users
 * Creates or retrieves a messaging channel for 1-on-1 communication.
 * If channel already exists with same ID, it returns the existing channel.
 * 
 * @param chatClient - Stream Chat client instance
 * @param myUserId - Current user's ID (sender)
 * @param receiverUserId - Other user's ID (receiver)
 * @param teamId - Team/organization ID
 * @param eventId - Event ID for filtering/organization
 * @param ChannelHashId - Unique channel ID (usually a hash of both user IDs)
 * @param type - Channel type, defaults to "messaging" for DMs
 * @returns Promise<Channel> - The created/retrieved channel
 * TYPICAL USAGE:
 * When user clicks on another participant to start a conversation
 */
async function createChannel(
  chatClient: StreamChat<DefaultGenerics>,
  myUserId: string,
  receiverUserId: string,
  teamId: string,
  eventId: number,
  ChannelHashId: string,
  type: string = "messaging"
) {
  // Create channel with specified ID and members
  const channel = chatClient.channel(type, ChannelHashId, {
    members: [myUserId, receiverUserId],
    team: teamId, eventId: eventId
  });
  
  // Watch channel to establish connection and load state
  await channel.watch();
  return channel;
}

/**
 * Finds and returns a specific channel based on different entity types.
 * Used when navigating to app from push notifications or deep links.
 * 
 * SUPPORTED ENTITY TYPES:
 * 1. Push - Direct channel lookup by CID (Channel ID)
 * 2. Watchlive - Find watch live channel for events
 * 3. Program/Session - Find public channel for specific program/session
 * 
 * FLOW FOR PUSH:
 * 1. Query all channels for the event
 * 2. Find channel matching the CID (entityId)
 * 
 * FLOW FOR WATCHLIVE:
 * 1. Query all watchlive channels for the event
 * 2. Parse channel details to find EntityType === "watchlive"
 * 3. Return first match
 * 
 * FLOW FOR PROGRAM:
 * 1. Query all public_without_membership channels (limit 30)
 * 2. Parse each channel's details JSON
 * 3. Match EntityType === "program" AND EntityId === entityId
 * 4. Return matching channel
 * 
 * @param chatClient - Stream Chat client instance
 * @param entityType - Type of entity (Push, Watchlive, or Program)
 * @param entityId - ID to match against (CID for Push, EntityId for others)
 * @param eventId - Event ID for filtering channels
 * @returns Promise<Channel | null> - Found channel or null if not found
 * 
 * TYPICAL USAGE:
 * When app opens from push notification with specific channel to show
 */
async function getChannel(
  chatClient: StreamChat<DefaultGenerics>,
  entityType: ModuleType,
  entityId: string,
  eventId: number
) {
  // CASE 1: Push notification - direct CID lookup
  if (entityType === ModuleType.Push) {
    const channelFilter = { eventId: eventId };
    const channelData = await chatClient.queryChannels(channelFilter);
    const channel = channelData.find(ele => ele.cid === entityId);
    return channel || null;
  }
  
  // CASE 2: Watchlive - find channel for live streaming
  else if(entityType === ModuleType.Watchlive){
    const watchliveChannelFilter = { type: 'public_without_membership_watchlive', eventId: eventId };
    const watchliveChannels = await chatClient.queryChannels(watchliveChannelFilter, undefined, { state: false, watch: false });

    // Search through watchlive channels for matching EntityType
    for (const channel of watchliveChannels) {
      if (channel) {
          const details = channel.data!.details;
          const parsedObject = JSON.parse(details as string);
          // Check if this is the watchlive channel
          if (parsedObject.EntityType.toLowerCase() === "watchlive") {
            return channel;
          }
      }
    }

    return null; // Return null if no matching channel is found
  }
  
  // CASE 3: Program/Session - find channel for specific program
  else {
    const publicWithoutChannelFilter = { type: 'public_without_membership', eventId: eventId };
    const publicWithoutChannels = await chatClient.queryChannels(publicWithoutChannelFilter, undefined, { state: false, watch: false, limit: 30 });

    // Search through public channels for matching program
    for (const channel of publicWithoutChannels) {
      if (channel) {
          const details = channel.data!.details;
          const parsedObject = JSON.parse(details as string);
          // Check if EntityType is "program" AND EntityId matches
          if (parsedObject.EntityType.toLowerCase() === "program" && parsedObject.EntityId == entityId) {
            return channel;
          }
      }
    }

    return null; // Return null if no matching channel is found
  }
}

const App = (props: AppProps) => {
  // EXTRACT PROPS - Unpack configuration from BootComponent  
  const { apiKey, credentials, targetOrigin, colors, pageType, interop, fontSize, platformTypes, language, onRemount } = props;
  
  /**
   * Get channel list options for querying channels
   * Uses userId and eventId to filter channels correctly
   */
  const channelListOptions = getChannelListOptions(credentials.UserId, credentials.EventId);
  
  /*Track if user is creating a new channel, Used to show/hide channel creation UI*/
  const [isCreating, setIsCreating] = useState(false);

  /* Determine if this is a session chat (Program module), Session chats hide the sidebar and show only the channel view*/
  const isSessionChat = pageType.EntityType === ModuleType.Program;
  
  /* Extract user credentials for easy access, These are used throughout the component for authentication and filtering*/
  let userId = credentials.UserId;
  let userToken = credentials.UserToken;
  let teamId = credentials.TeamId;
  const eventId = credentials.EventId;
  
  /* Connect to Stream Chat using credentials*/
  const { chatClient, connectError } = useConnectUser(apiKey, { id: userId, eventId: eventId }, userToken);

  /* ToggleMobile - Function to toggle mobile sidebar visibility, Returns function that shows/hides channel list on mobile*/
  const toggleMobile = useMobileView();
  
  /* ThemeClassName - CSS class for current theme (light/dark), Automatically updates when theme changes*/
  const { themeClassName } = useThemeContext();
  
  /**
   * selectedChannel - Currently active channel from context
   * setSelectedChannel - Function to update active channel
   * Used for tracking which channel is open
   */
  const { selectedChannel, setSelectedChannel } = useChannelContext();
  
  /**
   * activeSidebarTab - Track which tab is active in sidebar (DM or EventChat)
   * Initial value from pageType.OpenDMTab (true = DM tab, false = EventChat tab)
   */
  const [activeSidebarTab, setActiveSidebarTab] = useState<ChatType>(pageType.OpenDMTab ? ChatType.DM : ChatType.EventChat);
  
  /**
   * Select language file based on language code from props
   * SUPPORTED LANGUAGES:
   * - en: English
   * - da: Danish
   * - de: German
   * - es: Spanish
   * - fi: Finnish
   * - fr/fr-FR: French
   * - nb: Norwegian Bokmål
   * - sv: Swedish
   * Falls back to English if language not found
   */
  let languageCode: any;

  switch (language) {
    case 'en':
      languageCode = enLang;
      break;
    case 'da':
      languageCode = daLang;
      break;
    case 'de':
      languageCode = deLang;
      break;
    case 'es':
      languageCode = esLang;
      break;
    case 'fi':
      languageCode = fiLang;
      break;
    case 'fr':
    case 'fr-FR':
      languageCode = frLang;
      break;
    case 'nb':
      languageCode = nbLang;
      break;
    case 'sv':
      languageCode = svLang;
      break;
    default:
      languageCode = enLang;
  }
  
  /* useChecklist - Monitors user actions for feature discovery*/
  useChecklist(chatClient, targetOrigin);
  
  /* useUpdateAppHeightOnResize - Dynamically adjust app height, Handles viewport changes (keyboard open/close, window resize) */
  useUpdateAppHeightOnResize();
  
  /* Apply theme colors immediately, Sets CSS variables for background, foreground, and CTA colors*/
  setColors(colors);
  
  /* Apply font size when chat client is ready*/
  useEffect(() => {
    languageCode = language || enLang;
    setFontSize(fontSize);
  }, [chatClient]);

  /**
   * If Stream Chat connection failed for Program module, show error screen
   * 
   * CONDITIONS:
   * - chatClient is null (connection failed)
   * - EntityType is Program (session chat)
   * - connectError exists
   */
  if (
    !chatClient &&
    ModuleType.Program === pageType.EntityType &&
    connectError
  ) {
    interop.FinishedLoading(true);

    return (
      <div className='str-chat-text-msg'>
        <div className='str-chat-placeholder-text'>{languageCode['Something went wrong']}</div>
        <div className='str-chat-try-again' onClick={onRemount}>
          {languageCode['Try again in a few seconds']} <div className='str-chat-refresh-icon'><Refresh /></div>
        </div>
      </div>
    );
  }
  
  /**
   * If chat client hasn't connected yet, show nothing (loading)
   * This prevents rendering UI before Stream Chat is ready
   */
  if (!chatClient) {
    return null;
  }
  
  /**
   * Handle opening a specific channel on app start
   * 
   * SCENARIOS:
   * 1. User taps push notification → Opens that channel
   * 2. User clicks participant → Opens DM with that person
   * 3. User clicks program/session → Opens that program's channel
   * 
   * FLOW:
   * 1. Check if pageType is ChannelDetail (need to open specific channel)
   * 2. Switch to ChannelListing mode (show sidebar + channel)
   * 3. Create or get the channel based on EntityType
   * 4. Set as selectedChannel to open it
   */
  if (pageType.ChatPageType == ChatPageType.ChannelDetail) {
    // Switch to channel listing mode (shows sidebar)
    pageType.ChatPageType = ChatPageType.ChannelListing;
    
    // Opening DM with specific participant
    if (pageType.EntityType == ModuleType.Participant) {
      createChannel(chatClient!, userId, pageType.EntityId, teamId, eventId, pageType.ChannelHashId).then(channel => {
        // Only open if channel is not blocked
        if (channel.data?.blocked === false) {
          setSelectedChannel(channel);
        }
      });
    } 
    // Opening public channel
    else {
      getChannel(chatClient!, pageType.EntityType, pageType.EntityId, eventId).then(async channel => {
        if (channel) {
          // Watch channel to load messages and state
          await channel.watch();
          setSelectedChannel(channel);
        }
      });
    }
  }
  
  /**
   * Notify native/parent that app has finished loading
   * This allows native app to hide splash screen or loading indicator
   */
  interop.FinishedLoading(true);
  
  /**
   * Create Stream Chat i18n (internationalization) instance
   * Provides translations for all Stream Chat UI components
   * Uses the languageCode selected above
   */
  const i18nInstance = new Streami18n({
    translationsForLanguage: languageCode,
  });


  return (
    <BlockUnblockProvider>
      <AlertContextProvider>
      <Chat client={chatClient} i18nInstance={i18nInstance} theme={`messaging ${themeClassName}`}>
        {/* Always render sidebar to keep channel selection logic alive; hide visually for Program */}
        <div style={isSessionChat ? {display:'none'} : {display:'block'}} >
        <MessagingSidebar
          channelListOptions={channelListOptions}
          onClick={() => {
            toggleMobile();
            interop.SlidingGesture(false);
            interop.NavigationHandle(false); 
          }}
          onCreateChannel={()=>{
            interop.ToogleMenu();
          }
        }
          onTabChange={(tab) => setActiveSidebarTab(tab)}
          onPreviewSelect={() => setIsCreating(false)}
          activeChannel={selectedChannel}
          channelQueryParams={{ eventId }}
          platformType={platformTypes}
          languageCode={languageCode}
          eventName={credentials.EventName}
          moduleType={pageType.EntityType}
          groupIds={credentials.GroupIds}
          openDmTab={pageType.OpenDMTab}
          dmTabName={pageType.DMTabName}
          eventChatName={pageType.EventChatName}
        />
        </div>
        {selectedChannel ? (
          <ChannelUI
            maxNumberOfFiles={10}
            multipleUploads={true}
            SendButton={SendButton}
            ThreadHeader={(props) => <MessagingThreadHeader {...props} languageCode={languageCode} />}
            EmojiPicker={platformTypes==="Mobile" ? undefined : WrappedEmojiPicker}
            emojiSearchIndex={platformTypes==="Mobile" ? undefined : SearchIndex}
            DateSeparator={(props) => <DateSeparatorCustom {...props} languageCode={languageCode} />}
            UnreadMessagesNotification={() => null}
            AutocompleteSuggestionList={SuggestionListWithHeader}
            AutocompleteSuggestionItem={CustomAutocompleteSuggestionItem}
          >
            <GiphyContextProvider>
              <ChannelInner theme={themeClassName}
                toggleMobile={() => {
                  toggleMobile();
                  interop.SlidingGesture(true);
                  if (pageType.EntityId === "-1") {
                    interop.NavigationHandle(false);
                  } else {
                    interop.NavigationHandle(true);
                  }
                  interop.PushHandle("-1");
                }}
                interop={interop}
                platformType={platformTypes}
                languageCode={languageCode}
                pageType={pageType}
              />
            </GiphyContextProvider>
          </ChannelUI>
        ) : (
          isSessionChat ? (
            <div className='str-chat-text-msg'>
              {/* Program chat loading spinner */}
              <div className="spinner-loader">
                <img src={LoadingSpinner} alt="Loading..." style={{height: "50px", width: "50px"}}/>
              </div>
            </div>
          ) : (
            activeSidebarTab === ChatType.DM ? (
              <div className='str-chat-text-msg'>{languageCode['Select a participant from list to initiate chat']}</div>
            ) : (
              <div className='str-chat-text-msg'>{languageCode['Select a channel from the list to join and start chatting.']}</div>
            )
          )
        )}
      </Chat>
      </AlertContextProvider>
    </BlockUnblockProvider>
  );
};

export default App;
